// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { filter } from "../../../../../../../io/hyperify/core/functions/filter";
import { StringUtils } from "../../../../../../../io/hyperify/core/StringUtils";
import {
    PRODUCT_TABLE_ITEM_HEADER_CLASS_NAME,
    ATLAS_EXTRA_PRODUCT_VIEW_CLASS_NAME,
} from "../../../../../core/constants/className";
import { SendanorProductId } from "../../../../../core/types/SendanorProductId";
import { SendanorProductUtils } from "../../../../../core/utils/SendanorProductUtils";
import { ProductArticle, ProductArticleChildrenPlacement } from "../../../common/productArticle/ProductArticle";
import {
    T_INTRO_ORDER_NOW,
    T_DATE_TIME_FORMAT,
    T_ORDER_NOW,
    T_ATLAS_EXTRA_TITLE,
    T_ATLAS_EXTRA_DESCRIPTION,
    T_ATLAS_EXTRA_DISCOUNT_INFO,
    T_ATLAS_EXTRA_ORIGINAL_PRICE,
    T_ATLAS_EXTRA_SUMMARY,
    T_ATLAS_EXTRA_PRICE_INFO,
    T_VIRTUALSERVERS_MAIN_TITLE,
} from "../../../../../core/constants/translation";
import {
    ATLAS_EXTRA_PRODUCT_QUERY_PARAM_PRICE_TYPE,
    ATLAS_EXTRA_ROUTE,
} from "../../../../../core/constants/route";
import { getProductImage } from "../../../../assets/images";
import { Product } from "../../../../../../../io/hyperify/core/store/types/product/Product";
import { useStoreProductList } from "../../../../../../../io/hyperify/frontend/hooks/useStoreProductList";
import { ProductType } from "../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { parseProductPriceType, ProductPriceType } from "../../../../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS } from "../../../../../core/constants/frontend";
import { find } from "../../../../../../../io/hyperify/core/functions/find";
import { PriceTypeSelect } from "../../../common/priceTypeSelect/PriceTypeSelect";
import { ProductUtils } from "../../../../../../../io/hyperify/core/store/utils/ProductUtils";
import { ProductTableItemModel } from "../../../../../../../io/hyperify/core/store/types/product/ProductTableItemModel";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { SendanorSeoImageData } from "../../../../../core/types/SendanorSeoImageData";
import { SEODiv } from "../../../common/seo/SEODiv";
import { getSeoDataFromProductTableItem } from "../utils/getSeoDataFromProductTableItem";
import { useQueryParam } from "../../../../../../../io/hyperify/frontend/hooks/useQueryParams";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import "./AtlasExtraProductView.scss";

const MAX_SEO_DESCRIPTION_LENGTH: number = 200;
const MAX_SEO_EXTRA_INFO_LENGTH: number = 100;

const LOG = LogService.createLogger('AtlasExtraProductView');

export interface AtlasExtraProductViewProps {
    readonly t: TranslationFunction;
    readonly className?: string;
    readonly product : SendanorProductId;
    readonly odd ?: boolean;
}

export function AtlasExtraProductView (props: AtlasExtraProductViewProps) {

    const t: TranslationFunction = props?.t ?? ((key: string): string => key);
    const className = props?.className;
    const isOdd : boolean = props?.odd ?? false;
    const product = props.product;
    const dateTimeFormat = t(T_DATE_TIME_FORMAT);
    const [queryParamPriceType, setPriceType] = useQueryParam(ATLAS_EXTRA_PRODUCT_QUERY_PARAM_PRICE_TYPE, DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS);
    const priceType : ProductPriceType = parseProductPriceType(queryParamPriceType) ?? DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS;
    const productList: Product[] = useStoreProductList(ProductType.ATLAS_EXTRA);
    const productModel = product ? find(productList, (p: Product) : boolean => p.id === product) : undefined;
    const productPrice = productModel && productModel.prices.length >= 1 ? find(productModel.prices, (price) => price.type === priceType) : undefined;

    LOG.debug(`Atlas extra products: `, productList, productModel, productPrice);

    const translationParams = SendanorProductUtils.createTranslationParamsFromPrice(
        productPrice,
        dateTimeFormat,
        t,
    );

    // This goes to the web page
    const description: string[] = (
        translationParams.DISCOUNT ?
            [
                T_ATLAS_EXTRA_PRICE_INFO,
                T_ATLAS_EXTRA_DISCOUNT_INFO,
                T_ATLAS_EXTRA_ORIGINAL_PRICE,
                T_ATLAS_EXTRA_DESCRIPTION(product),
            ]
            :
            [
                T_ATLAS_EXTRA_PRICE_INFO,
                T_ATLAS_EXTRA_DESCRIPTION(product),
            ]

    );

    // This goes to the SEO image
    const descriptionNoWebsite: string[] = (
        translationParams.DISCOUNT ?
            [
                T_ATLAS_EXTRA_SUMMARY(product),
                T_ATLAS_EXTRA_PRICE_INFO,
                T_ATLAS_EXTRA_DISCOUNT_INFO,
                T_ATLAS_EXTRA_ORIGINAL_PRICE,
                // T_ATLAS_EXTRA_DESCRIPTION(product),
            ]
            :
            [
                T_ATLAS_EXTRA_SUMMARY(product),
                T_ATLAS_EXTRA_PRICE_INFO,
                // T_ATLAS_EXTRA_DESCRIPTION(product),
            ]
    );

    // This goes to the SEO image
    const seoDescriptionText : string = ComponentUtils.prepareParagraphNodesAsText(
        t,
        descriptionNoWebsite,
        undefined,
        translationParams
    );

    const productTableItemsList: ProductTableItemModel[] = ProductUtils.createProductTableItemsList(
        t(T_ATLAS_EXTRA_TITLE(product), translationParams), // This is appended to the SEO description and shopping cart
        t(T_ATLAS_EXTRA_SUMMARY(product), translationParams), // This is appended to the SEO description and in the shopping cart also
        priceType,
        filter(productList, (item) => item.id === product),
    );

    const priceTypeOptions : ProductPriceType[] = ProductUtils.createUniquePriceTypeList(productTableItemsList);

    const seoMainDescriptionText = t(T_ATLAS_EXTRA_DESCRIPTION(product), translationParams);

    // Use one item by random as the seo image featured item
    // const [, ...featuredItems] = productTableItemsList;
    // const seoImageItem : ProductTableItemModel | undefined = first(shuffle(featuredItems));
    const seoFeaturedItemImageData : SendanorSeoImageData = getSeoDataFromProductTableItem(
        t,
        undefined, // Item title and description is read from here
        T_VIRTUALSERVERS_MAIN_TITLE, // This is the left side text under the logo
        T_ORDER_NOW,
        `${t(T_ATLAS_EXTRA_TITLE(product), translationParams)} ${t(T_ATLAS_EXTRA_SUMMARY(product), translationParams)}`, // The title of the preview
        StringUtils.truncateLongString(seoDescriptionText, MAX_SEO_EXTRA_INFO_LENGTH, '...'),
        false,
        translationParams,
        StringUtils.truncateLongString(seoMainDescriptionText, MAX_SEO_DESCRIPTION_LENGTH, '...'),
    );

    const routePath = ATLAS_EXTRA_ROUTE(product);
    const productImage : string = getProductImage(product);

    return (
        <SEODiv className={
            ATLAS_EXTRA_PRODUCT_VIEW_CLASS_NAME
            + (className ? ` ${className}` : "")
        }
                path={routePath}
                title={seoFeaturedItemImageData.title ?? ''}
                description={seoFeaturedItemImageData.description ?? ''}
                tagLine={seoFeaturedItemImageData.tagLine}
                price={seoFeaturedItemImageData.price}
                buttonText={seoFeaturedItemImageData.buttonText}
                discountText={seoFeaturedItemImageData.discountText}
                extraInfo={seoFeaturedItemImageData.extraInfo}
        >

            <article className={
                ATLAS_EXTRA_PRODUCT_VIEW_CLASS_NAME+'-article ' +
                `${ATLAS_EXTRA_PRODUCT_VIEW_CLASS_NAME}-${isOdd ? 'odd' : 'even'}-article`
            }>

                <ProductArticle
                    t={t}
                    className={ATLAS_EXTRA_PRODUCT_VIEW_CLASS_NAME+'-article-content'}
                    previewImage={productImage}
                    title={
                        `${t(T_ATLAS_EXTRA_TITLE(product), translationParams)} (${
                            t(T_ATLAS_EXTRA_SUMMARY(product), translationParams)
                        })`
                    }
                    description={description}
                    buyButtonProduct={productModel}
                    buyButtonPrice={productPrice}
                    buyButtonText={t(T_INTRO_ORDER_NOW, translationParams)}
                    translationParams={translationParams}
                    childrenPlacement={ProductArticleChildrenPlacement.BEFORE_BUTTONS}
                >

                    <PriceTypeSelect
                        t={t}
                        className={`${PRODUCT_TABLE_ITEM_HEADER_CLASS_NAME}-select-price`}
                        value={priceType}
                        setValue={setPriceType}
                        options={priceTypeOptions}
                    />

                </ProductArticle>
            </article>

        </SEODiv>
    );
}
